import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { rccColors } from "../colors";
import { useRccTypography } from "../typography";

const FONT_FAMILY = '"Onest", sans-serif';

/**
 * D3MultiLineChart - Modern Multi-Line Chart with Dots
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data objects
 * e.g. [{ quarter: "2024-Q4", ">$1B": 48, "<=$100M": 50, ... }, ...]
 * @param {Array} props.categories - Keys to plot lines for (e.g. [">$1B", "<=$100M"])
 * @param {Object} props.colorPalette - Map of category -> color hex code
 */
function D3MultiLineChart({ data, categories, colorPalette, options = {} }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const typography = useRccTypography();

  const {
    yAxisFormatter = (v) => v,
    xAxisFormatter = (v) => v,
    marginTop = 20,
    marginRight = 30,
    marginBottom = 40,
    marginLeft = 40,
    showLegend = true,
  } = options;

  const getColorMap = () => {
    const map = {};

    // We assume colorPalette is always an array of { color, textColor } objects
    if (Array.isArray(colorPalette) && colorPalette.length > 0) {
      categories.forEach((cat, idx) => {
        // Cycle through the fixed palette
        const paletteItem = colorPalette[idx % colorPalette.length];

        // We only need the main color for lines, but we extract safely
        map[cat] = paletteItem.color || paletteItem;
      });
    } else {
      // Fallback only if the prop is missing or empty
      categories.forEach((cat) => {
        map[cat] = rccColors.neutral[400];
      });
    }
    return map;
  };

  const colorMap = getColorMap();

  console.log("D3MultiLineChart Render:", {
    data,
    categories,
    colorMap,
    options,
  });

  useEffect(() => {
    if (!wrapperRef.current || !containerRef.current || !data?.length) return;

    const renderChart = () => {
      const wrapper = wrapperRef.current;
      const container = containerRef.current;

      const containerWidth = wrapper.clientWidth;
      const containerHeight = wrapper.clientHeight;

      const width = containerWidth - marginLeft - marginRight;
      const height = containerHeight - marginTop - marginBottom;

      if (width <= 0 || height <= 0) return;

      // Clear SVG
      d3.select(container).selectAll("*").remove();

      const svg = d3
        .select(container)
        .append("svg")
        .attr("width", containerWidth)
        .attr("height", containerHeight)
        .append("g")
        .attr("transform", `translate(${marginLeft},${marginTop})`);

      // --- 1. Identify Valid Categories ---
      const visibleCategories = categories.filter((cat) =>
        data.some((d) => {
          const val = Number(d[cat]);
          return !isNaN(val) && val > 0;
        })
      );

      // --- Ignore "Total" when deciding to show labels ---
      // If we have ["Unknown", "Total"], filtering "Total" leaves 1.
      // 1 <= 1 is true, so labels will show.
      const realCategories = visibleCategories.filter((c) => c !== "Total");
      const shouldShowLabels = realCategories.length <= 1;

      // 1. Scales
      const xDomain = data.map((d) => d.quarter || d.xAxisLabel);
      const xScale = d3
        .scalePoint()
        .domain(xDomain)
        .range([0, width])
        .padding(0.5);

      const allValues = data.flatMap((d) => categories.map((k) => d[k] || 0));
      const maxY = d3.max(allValues) * 1.1; // Add 10% headroom
      const yScale = d3.scaleLinear().domain([0, maxY]).range([height, 0]);

      // 2. Gridlines (Horizontal Only)
      const yAxis = d3
        .axisLeft(yScale)
        .ticks(5)
        .tickSize(-width)
        .tickFormat("");
      const gridGroup = svg.append("g").attr("class", "grid");

      gridGroup.call(yAxis);
      gridGroup.select(".domain").remove();
      gridGroup
        .selectAll("line")
        .attr("stroke", rccColors.neutral[200]) // Light grey grid
        .attr("stroke-dasharray", "0"); // Solid lines

      // 3. X-Axis
      const xAxis = d3.axisBottom(xScale).tickFormat(xAxisFormatter);
      const xAxisGroup = svg
        .append("g")
        .attr("transform", `translate(0,${height})`)
        .call(xAxis);

      xAxisGroup.select(".domain").attr("stroke", rccColors.neutral[200]);
      xAxisGroup.selectAll("line").remove(); // No tick marks on X axis
      xAxisGroup
        .selectAll("text")
        .attr("y", 15)
        .attr("class", typography.textExtraSmallRegular)
        .style("fill", rccColors.neutral[800])
        .style("font-family", FONT_FAMILY);

      // 4. Y-Axis Labels
      const yLabelAxis = d3
        .axisLeft(yScale)
        .ticks(5)
        .tickFormat(yAxisFormatter);
      const yLabelGroup = svg.append("g").call(yLabelAxis);

      yLabelGroup
        .select(".domain")
        .attr("stroke", rccColors.neutral[100])
        .attr("stroke-width", 1);
      yLabelGroup.selectAll("line").remove();
      yLabelGroup
        .selectAll("text")
        .attr("x", -10)
        .attr("class", typography.textExtraSmallRegular)
        .style("fill", rccColors.neutral[800])
        .style("font-family", FONT_FAMILY);

      // 5. Draw Lines
      const lineGenerator = d3
        .line()
        .x((d) => xScale(d.quarter || d.xAxisLabel))
        .y((d) => yScale(d.value));

      categories.forEach((category) => {
        const color = colorMap[category] || rccColors.neutral[500];

        // Prepare data for this specific line
        // Filter out null/undefined points to avoid breaks
        const lineData = data
          .map((d) => ({
            quarter: d.quarter || d.xAxisLabel,
            value: d[category],
          }))
          .filter((pt) => pt.value !== undefined && pt.value !== null);

        // Draw Line
        svg
          .append("path")
          .datum(lineData)
          .attr("fill", "none")
          .attr("stroke", color)
          .attr("stroke-width", 2)
          .attr("d", lineGenerator);

        // Draw Dots
        svg
          .selectAll(`.dot-${category.replace(/[^a-zA-Z0-9]/g, "")}`)
          .data(lineData)
          .enter()
          .append("circle")
          .attr("cx", (d) => xScale(d.quarter))
          .attr("cy", (d) => yScale(d.value))
          .attr("r", 6) // Large radius
          .attr("fill", color)
          .attr("stroke", "#fff") // Optional white border if needed
          .attr("stroke-width", 0);

        // --- UPDATED LOGIC: Show labels if enabled or only 1 visible category ---
        if (shouldShowLabels) {
          svg
            .selectAll(`.label-${category.replace(/[^a-zA-Z0-9]/g, "")}`)
            .data(lineData)
            .enter()
            .append("text")
            .attr("x", (d) => xScale(d.quarter))
            .attr("y", (d) => yScale(d.value) - 12)
            .attr("text-anchor", "middle")
            .attr("class", typography.textExtraSmallSemibold)
            .style("fill", rccColors.neutral[800])
            .text((d) => yAxisFormatter(d.value));
        }
      });
    };

    renderChart();

    const resizeObserver = new ResizeObserver(() => {
      // Small debounce
      setTimeout(renderChart, 50);
    });
    resizeObserver.observe(wrapperRef.current);

    return () => resizeObserver.disconnect();
  }, [
    data,
    categories,
    colorPalette,
    options,
    marginLeft,
    marginTop,
    marginBottom,
    marginRight,
    colorMap,
  ]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        ref={wrapperRef}
        style={{
          width: "100%",
          flex: 1,
          position: "relative",
          minHeight: 0,
        }}
      >
        <div
          ref={containerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            margin: 0,
            padding: 0,
          }}
        />
      </div>
      {showLegend && (
        <div
          style={{
            // 1. OUTER CONTAINER: Defines the available space and centers the inner block
            display: "flex",
            justifyContent: "center", // This centers the "Inner Block"
            width: "100%",
            paddingTop: "16px",

            // Apply chart margins so the center point matches the chart's visual center
            paddingLeft: `${marginLeft}px`,
            paddingRight: `${marginRight}px`,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              // 2. INNER BLOCK: Tries to be only as wide as the content
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-start",
              gap: "8px 32px",
              maxWidth: "100%", // Ensures it doesn't overflow
            }}
          >
            {categories.map((category) => (
              <div
                key={category}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor:
                      colorMap[category] || rccColors.neutral[400],
                    flexShrink: 0,
                  }}
                />
                <span
                  className={typography.textSmallRegular}
                  style={{
                    color: rccColors.neutral[800],
                    whiteSpace: "nowrap",
                  }}
                >
                  {category}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default D3MultiLineChart;
